import AlertService from "../../../../services/AlertService";
import AssessmentMasterService from "../../../../services/AssessmentMasterService";
import CourseService from "../../../../services/CourseService";
import AuthContext from "../../../../auth/AuthContext";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EditMCQIcon from "../../../../assets/icons/edit.svg";
import WhiteEditMCQIcon from "../../../../assets/icons/WhiteEditIcon.svg";
import { Button, Col, Form, Row } from "react-bootstrap";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { FaTrash } from "react-icons/fa";

const Assessment = () => {
  const navigate = useNavigate();
  const { assessmentName } = useParams();
  const { state } = useLocation();
  const storedId = sessionStorage.getItem("assessmentId");
  const id = state?.id || storedId;
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [scheduledDate, setScheduledDate] = useState("");
  const submitControllerRef = useRef(null);

  const times = Array.from({ length: 60 }, (_, i) => ({
    value: (i + 1) * 5,
    label: `${(i + 1) * 5} mins`,
  }));

  // States
  const [assessmentId, setAssessmentId] = useState(0);
  const [assName, setAssName] = useState("");
  const [courseId, setCourseId] = useState("");
  const [mcq, setMCQ] = useState(false);
  const [practical, setPractical] = useState(false);
  const [reviewable, setReviewable] = useState(true);
  const [duration, setDuration] = useState("");
  const [practicalDuration, setPracticalDuration] = useState("");
  const [toBeProvided, setToBeProvided] = useState("");
  const [totalPracticalMarks, setTotalPracticalMarks] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const authContext = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  // Permissions
  useEffect(() => {
    const assessmentAccess = authContext.getPermissions("assessmentsmaster");
    if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: assessmentAccess.some((f) => f.access.includes(2)),
      showEdit: assessmentAccess.some((f) => f.access.includes(3)),
      showDelete: assessmentAccess.some((f) => f.access.includes(4)),
    });
  }, [authContext, navigate]);

  // Load Courses
const loadCourses = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetCourses(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort(); 
  }, [loadCourses]);

const loadAssessment = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await AssessmentMasterService.Get(id, authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const d = data.data;
        setAssName(d.assessmentName || "");
        setCourseId(d.courseId?.toString() || "");
        setMCQ(d.mcq);
        setPractical(d.practical);
        setDuration(d.duration?.toString() || "");
        setPracticalDuration(d.practicalDuration?.toString() || "");
        setToBeProvided(d.toBeProvided?.toString() || "");
        setReviewable(d.reviewAvailable);
        setTotalPracticalMarks(d.totalPracticalScore?.toString() || "");
        setScheduledDate(
          d.scheduledDate
            ? new Date(d.scheduledDate).toISOString().split("T")[0]
            : ""
        );
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, id]);

useEffect(() => {
    const controller = new AbortController(); 
    if (id) {
      setIsEditing(true);
      setAssessmentId(id);
      loadAssessment(controller.signal); 
    }
    return () => controller.abort();
  }, [loadAssessment, id]);

  // Validation
  const validate = () => {
    const newErrors = {};

    if (!assName.trim()) {
      newErrors.assName = "Assessment Name is required";
    } else if (assName.trim().length < 3) {
      newErrors.assName = "Assessment Name must be at least 3 characters";
    }

    if (!courseId) {
      newErrors.courseId = "Please select a course";
    }

    if (!mcq && !practical) {
      newErrors.assessmentType =
        "At least one assessment type (MCQ or Practical) is required";
    }

    if (mcq) {
      if (!toBeProvided || toBeProvided <= 0) {
        newErrors.toBeProvided = "Please enter the number of questions";
      }
      if (!duration) {
        newErrors.duration = "MCQ duration is required";
      }
    }

    if (practical) {
      if (!totalPracticalMarks || totalPracticalMarks <= 0) {
        newErrors.totalPracticalMarks =
          "Total practical marks must be greater than 0";
      }
      if (!practicalDuration) {
        newErrors.practicalDuration = "Practical duration is required";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit


const onSubmitHandler = async (event) => {
  event.preventDefault();

  if (!validate()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      assessmentName: assName.trim(),
      courseId: Number(courseId),
      mcq,
      practical,
      duration: duration ? Number(duration) : 0,
      practicalDuration: practicalDuration ? Number(practicalDuration) : 0,
      toBeProvided: toBeProvided ? Number(toBeProvided) : 0,
      reviewAvailable: reviewable,
      totalPracticalScore: totalPracticalMarks
        ? Number(totalPracticalMarks)
        : 0,
      scheduledDate: scheduledDate || null,
    };

    if (assessmentId > 0) payload.id = assessmentId;

    const data = await AssessmentMasterService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Assessment saved successfully!");
      navigate("/masters-assessments");
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const onConfirmDeleteHandler = () => setShowDeleteDialog(true);
  const onDeleteHandler = async () => {
    const data = await AssessmentMasterService.Remove(
      assessmentId,
      authContext
    );
    if (data.isSuccess) {
      AlertService.success("Assessment deleted successfully!");
      navigate("/masters-assessments");
      sessionStorage.removeItem("assessmentId");
    } else {
      AlertService.error(data.errorMessage);
    }
  };

  const onEditMCQ = () => {
    navigate(`/masters-assessments/${assessmentName}/mcq`, { state: { id } });
  };

  const onEditPractical = () => {
    navigate(`/masters-assessments/${assessmentName}/practical`, {
      state: { id },
    });
  };

  return (
    <div className="container-fluid custom-container mt-3 mb-lg-4">
      {/* Edit Buttons */}
      {permissions.showEdit && assessmentId > 0 && (
        <div className="d-flex flex-wrap justify-content-end mb-3 gap-3 gap-md-4">
          {mcq && (
            <button
              className="me-4 mb-2 text-primary d-flex align-items-center card-btn-link"
              onClick={onEditMCQ}
            >
              <img
                src={isDark ? WhiteEditMCQIcon : EditMCQIcon}
                alt="Edit MCQ"
                className="w-4 h-4 me-2"
              />
              Edit MCQ
            </button>
          )}
          {practical && (
            <button
              className=" me-4 mb-2 text-primary d-flex align-items-center card-btn-link"
              onClick={onEditPractical}
            >
              <img
                src={isDark ? WhiteEditMCQIcon : EditMCQIcon}
                alt="Edit Practical"
                className="w-4 h-4 me-2"
              />
              Edit Practical
            </button>
          )}
        </div>
      )}

      <Form onSubmit={onSubmitHandler} className="ms-2 mt-4" noValidate>
        {/* Assessment Name - MANDATORY */}
        <Form.Group as={Row} className="mb-4 pb-2">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Assessment Name <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Col md={10}>
            <Form.Control
              type="text"
              value={assName}
              onChange={(e) => {
                setAssName(e.target.value);
                setErrors({ ...errors, assName: undefined });
              }}
              disabled={loading}
              isInvalid={!!errors.assName}
              className="custom-input custom-input-color"
            />
            <Form.Control.Feedback type="invalid">
              {errors.assName}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        {/* Course - MANDATORY */}
        <Form.Group as={Row} className="mb-4 pb-2">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Course Name <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Col md={10}>
            <Form.Select
              value={courseId}
              onChange={(e) => {
                const value = e.target.value;
                setCourseId(value);
                setErrors({ ...errors, courseId: undefined });
              }}
              disabled={loading}
              isInvalid={!!errors.courseId}
              className="custom-input custom-input-color"
            >
              <option value="" disabled>
                Select Course
              </option>
              {courses.map((c, i) => (
                <option key={i} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Form.Select>

            <Form.Control.Feedback type="invalid">
              {errors.courseId}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        {/* Scheduled Date */}
        <Form.Group as={Row} className="mb-4">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Scheduled Date
          </Form.Label>
          <Col md={4}>
            <Form.Control
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onFocus={(e) => {
              if (e.target.showPicker) {
              e.target.showPicker();
              }
              }}
              className="custom-input custom-input-color"
            />
          </Col>
        </Form.Group>

        {/* Assessment Type */}
        <Form.Group as={Row} className="mb-4">
          <Form.Label
            column
            md={2}
            className="fw-light pt-0 d-flex align-items-start"
          >
            Assessment Type<span className="text-danger ms-1 fs-6">*</span>
          </Form.Label>

          <Col md={10}>
            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                label="MCQ"
                checked={mcq}
                className=" me-4"
                onChange={(e) => {
                  setMCQ(e.target.checked);
                  if (errors.assessmentType) {
                    setErrors((prev) => ({
                      ...prev,
                      assessmentType: undefined,
                    }));
                  }
                }}
              />
              <Form.Check
                type="checkbox"
                label="Practical"
                checked={practical}
                onChange={(e) => {
                  setPractical(e.target.checked);
                  if (errors.assessmentType) {
                    setErrors((prev) => ({
                      ...prev,
                      assessmentType: undefined,
                    }));
                  }
                }}
              />
            </div>

            {errors.assessmentType && (
              <div className="text-danger ">
                <small className="text-danger">{errors.assessmentType}</small>
              </div>
            )}
          </Col>
        </Form.Group>

        {/* MCQ Fields - Required only if MCQ checked */}
        {mcq && (
          <>
            <Form.Group as={Row} className="mb-4 pb-2">
              <Form.Label
                column
                md={2}
                className="d-flex align-items-center fw-light"
              >
                Questions to be Presented
                <span className="text-danger ms-1 fs-6">*</span>
              </Form.Label>
              <Col md={10}>
                <Form.Control
                  type="number"
                  value={toBeProvided}
                  onChange={(e) => {
                    setToBeProvided(e.target.value);
                    setErrors({ ...errors, toBeProvided: undefined });
                  }}
                  disabled={loading}
                  isInvalid={!!errors.toBeProvided}
                  className="custom-input custom-input-color"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.toBeProvided}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-4 pb-2">
              <Form.Label
                column
                md={2}
                className="d-flex align-items-center fw-light"
              >
                MCQ Duration<span className="text-danger ms-1 fs-6">*</span>
              </Form.Label>
              <Col md={10}>
                <Form.Select
                  value={duration}
                  onChange={(e) => {
                    setDuration(e.target.value);
                    setErrors({ ...errors, duration: undefined });
                  }}
                  disabled={loading}
                  isInvalid={!!errors.duration}
                  className="custom-input custom-input-color"
                >
                  <option value="" disabled>
                    Select Duration
                  </option>
                  {times.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.duration}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          </>
        )}

        {/* Practical Fields - Required only if Practical checked */}
        {practical && (
          <>
            <Form.Group as={Row} className="mb-4 pb-2">
              <Form.Label
                column
                md={2}
                className="d-flex align-items-center fw-light"
              >
                Total Practical Marks
                <span className="text-danger ms-1 fs-6">*</span>
              </Form.Label>
              <Col md={10}>
                <Form.Control
                  type="number"
                  value={totalPracticalMarks}
                  onChange={(e) => {
                    setTotalPracticalMarks(e.target.value);
                    setErrors({ ...errors, totalPracticalMarks: undefined });
                  }}
                  disabled={loading}
                  isInvalid={!!errors.totalPracticalMarks}
                  className="custom-input custom-input-color"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.totalPracticalMarks}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label
                column
                md={2}
                className="d-flex align-items-center fw-light"
              >
                Practical Duration
                <span className="text-danger ms-1 fs-6">*</span>
              </Form.Label>
              <Col md={10}>
                <Form.Select
                  value={practicalDuration}
                  onChange={(e) => {
                    setPracticalDuration(e.target.value);
                    setErrors({ ...errors, practicalDuration: undefined });
                  }}
                  isInvalid={!!errors.practicalDuration}
                  disabled={loading}
                  className="custom-input custom-input-color"
                >
                  <option value="" disabled>
                    Select Duration
                  </option>
                  {times.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.practicalDuration}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          </>
        )}

        {/* Review Available */}
        {(mcq || practical) && (
          <Form.Group as={Row} className="mb-4">
            <Form.Label column md={2} className="fw-light"></Form.Label>
            <Col md={10}>
              <Form.Check
                type="checkbox"
                label="Review Available"
                checked={reviewable}
                onChange={(e) => setReviewable(e.target.checked)}
                disabled={loading}
              />
            </Col>
          </Form.Group>
        )}

        {/* Buttons */}
        <Row>
          <Col md={{ span: 10, offset: 2 }}>
            <div className="d-flex gap-3">
              {permissions.showDelete && assessmentId !== 0 && (
                <Button
                  variant="outline-danger"
                  className="px-4 px-md-5 py-2"
                  onClick={onConfirmDeleteHandler}
                  disabled={loading}
                  type="button"
                >
                  <FaTrash className="me-2 mb-1" />
                  Delete
                </Button>
              )}
              <div className="ms-auto d-flex gap-3">
                <Button
                  variant="outline-secondary"
                  className="px-4 px-md-5 py-2 btn btn-outline"
                  onClick={() => navigate("/masters-assessments")}
                  type="button"
                >
                  Cancel
                </Button>
                {((permissions.showAdd && assessmentId === 0) ||
                  (permissions.showEdit && assessmentId !== 0)) && (
                  <Button
                    variant="primary"
                    className="px-4 px-md-5 py-2"
                    disabled={loading}
                    type="submit"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Delete Confirm Dialog */}
      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDeleteHandler();
        }}
        title={
          <>
            Are you sure you want to <strong>delete</strong> this assessment?
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Assessment;
